import React, { useState, useMemo } from 'react';
import { produce } from 'immer';
import { UnfoldVertical } from 'lucide-react';
import SchemaViewer from '../NewWorkflow/shared/SchemaViewer';
import { fieldsToSchema, schemaToFields } from '../NewWorkflow/utils/schemaUtils';

// Helper to flatten schemas, now with a filter option
const flattenSchemaToFields = (rootFields, filterFn = () => true) => {
    const fields = [];
    const recurse = (nodes, pathPrefix, displayPrefix) => {
        for (const node of nodes) {
            const newPath = pathPrefix ? `${pathPrefix}.${node.fieldName}` : node.id;
            const newDisplay = displayPrefix ? `${displayPrefix}.${node.fieldName}` : node.fieldName;

            const fieldData = { ...node, path: newPath, display: newDisplay };
            if (filterFn(fieldData)) {
                fields.push(fieldData);
            }

            if (node.children && node.children.length > 0) {
                recurse(node.children, newPath, newDisplay);
            }
        }
    };
    recurse(rootFields, '', '');
    return fields;
};


const UnwrapArrayNodeModal = ({ mode, node, nodes, edges, onSave, onClose, globalState, onDelete }) => {
    const [selectedArrayPath, setSelectedArrayPath] = useState(node.data.selectedArrayPath || null);

    const inputRootFields = useMemo(() => {
        // return edges
        //     .filter(edge => edge.target === node.id)
        //     .map(edge => nodes.find(n => n.id === edge.source))
        //     .filter(Boolean)
        //     .map(parentNode => {
        //         const schema = parentNode?.data?.jsonOutputSchema;
        //         if (!schema) return null;
        //         return {
        //             id: `input_${parentNode.id}`,
        //             fieldName: parentNode.data.label || 'Input Node',
        //             dataType: 'Object',
        //             children: schemaToFields(schema, ''),
        //         };
        //     }).filter(Boolean);
        const connectedInputNodes = edges
            .filter(edge => edge.target === node.id)
            .map(edge => nodes.find(n => n.id === edge.source))
            .filter(Boolean)
            .filter(parentNode => parentNode.type !== 'loopBreak') // ⭐ ADD THIS LINE
            .map(parentNode => {
                let schema;
                if (parentNode.data.jsonOutput && parentNode.data.jsonOutputSchema?.properties) {
                    schema = parentNode.data.jsonOutputSchema;
                } else {
                    schema = {
                        title: 'Simple Output', type: 'object',
                        properties: { 'output': { type: 'string', description: `Output from "${parentNode.data.label || parentNode.type}"` } }
                    };
                }
                return {
                    id: `input_${parentNode.id}`, fieldName: parentNode.data.label || 'Input Node',
                    dataType: 'Object', children: schemaToFields(schema, ''),
                };
            }).filter(Boolean);

        // ⭐ 2. Create a "virtual node" for the Global State if it exists
        const globalStateNode = [];
        if (globalState && globalState.fields.length > 0) {
            const globalSchema = fieldsToSchema(globalState.fields, "globalState");
            globalStateNode.push({
                id: 'globalState', // Special, reserved ID
                fieldName: 'Global State',
                dataType: 'Object',
                children: schemaToFields(globalSchema, ''),
            });
        }

        // ⭐ 3. Combine the Global State with the other input nodes
        return [...globalStateNode, ...connectedInputNodes];
    }, [node.id, nodes, edges]);

    // We get a flat list of all fields to easily find the selected one's details
    const allInputFields = useMemo(() => flattenSchemaToFields(inputRootFields), [inputRootFields]);

    const handleFieldClick = (path, field) => {
        console.log("Clicked field:", path, field);
        setSelectedArrayPath(path);
    };

    // This function tells the SchemaViewer to only allow array fields to be clickable
    const isFieldValid = (field) => field.dataType.toLowerCase() === 'array';

    // Auto-generate the output schema based on the selected array
    const { jsonOutputSchema, outputRootField, outputFields } = useMemo(() => {
        const defaultReturn = {
            jsonOutputSchema: { title: 'ArrayElementOutput', type: 'object', properties: {} },
            outputRootField: [],
            outputFields: []
        };

        if (!selectedArrayPath) {
            return defaultReturn;
        }

        const selectedArrayField = allInputFields.find(f => f.path === selectedArrayPath);
        if (!selectedArrayField) {
            return defaultReturn;
        }

        const arrayElementField = {
            id: 'arrayElement',
            fieldName: 'arrayElement',
            dataType: selectedArrayField.arrayItemType,
            children: selectedArrayField.children,
            arrayItemType: 'String',
        };

        // This is the array we need to save.
        const finalOutputFields = [arrayElementField];
        const schema = fieldsToSchema(finalOutputFields);

        const rootFieldForViewer = [{
            id: 'root',
            fieldName: 'Output',
            dataType: 'Object',
            children: finalOutputFields
        }];

        return { jsonOutputSchema: schema, outputRootField: rootFieldForViewer, outputFields: finalOutputFields };

    }, [selectedArrayPath, allInputFields]);

    const handleSaveAndClose = () => {
        onSave({
            ...node,
            data: {
                ...node.data,
                label: `Unwrap: ${selectedArrayPath?.split('.').pop() || ''}`,
                selectedArrayPath,
                jsonOutputSchema: jsonOutputSchema,
                // ⭐ 2. Add the generated outputFields to the saved data.
                outputFields,
                jsonOutput: true
            },
        });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={onClose}>
            {/* ⭐ THE FIX: Using the wider `max-w-7xl` and fixed `h-[90vh]` classes */}
            <div className="bg-slate-800 w-full max-w-7xl h-[90vh] rounded-xl shadow-2xl border border-slate-700 flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-slate-700 flex-shrink-0">
                    <h2 className="text-xl font-semibold text-white flex items-center gap-2"><UnfoldVertical /> Configure Unwrap Array Node</h2>
                </div>

                <div className="flex-grow grid grid-cols-2 gap-4 p-4 overflow-hidden">
                    {/* Left Pane: Select an array */}
                    <div className="flex flex-col gap-2 overflow-y-auto pr-2">
                        <h3 className="text-lg font-semibold text-slate-200 flex-shrink-0">Select an Array Field</h3>
                        <p className="text-sm text-slate-400 flex-shrink-0">Only array fields are enabled. Click one to select it.</p>
                        <SchemaViewer
                            rootFields={inputRootFields}
                            onFieldClick={handleFieldClick}
                            isValidField={isFieldValid}
                            selectedPath={selectedArrayPath}
                        />
                    </div>

                    {/* Right Pane: View the output */}
                    <div className="flex flex-col gap-2 overflow-y-auto pl-2">
                        <h3 className="text-lg font-semibold text-slate-200 flex-shrink-0">Generated Output Element</h3>
                        <p className="text-sm text-slate-400 flex-shrink-0">The output will be a single field named `arrayElement` with the following structure.</p>
                        <SchemaViewer rootFields={outputRootField} />
                    </div>
                </div>

                <div className="p-4 flex justify-between items-center bg-zinc-800/50 border-t border-zinc-700 rounded-b-lg flex-shrink-0">
                    {mode === 'edit' && node?.id && (
                        <button onClick={() => onDelete(node.id)} className="px-4 py-2 text-sm font-semibold text-red-400 bg-red-900/50 hover:bg-red-900/80 rounded-md transition-colors">
                            Delete Node
                        </button>
                    )}
                    <div className="flex-grow" />
                    <div className="flex items-center gap-3">
                        <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-300 bg-zinc-700 hover:bg-zinc-600 rounded-md transition-colors">Cancel</button>
                        <button onClick={handleSaveAndClose} className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 rounded-md transition-colors">Save Changes</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnwrapArrayNodeModal;